import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Clear from "@material-ui/icons/Clear";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import TagNameSearchAutocomplete from "../../Components/TagNameSearchAutocomplete";
import Title from "../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getFromStorage,
  getToken,
  getWarehouses,
  requestErrorHandler,
  saveToStorage,
  STOCK_SEARCH_ID,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import AddStockItemDialog from "./AddNewStockItemDialog";
import EditStockItemDialog from "./EditStockItemDialog";

const server = EnvSettings.server;

async function search(stock, searchValue) {
  var array = [];
  if (searchValue.includes(",")) {
    var servallist = searchValue.split(",");
    servallist = servallist.filter((ser) => ser.trim() !== "");
    array = stock.filter((s) => {
      return servallist.some(
        (ser) =>
          s.StockItem.name.toLowerCase().includes(ser.toLowerCase()) ||
          s.StockItem.sku.toLowerCase().includes(ser.toLowerCase()) ||
          s.Warehouse.name.toLowerCase().includes(ser.toLowerCase()) ||
          s.StockItem.StockItemGroups.map((ss) =>
            ss.StockItemGroupName.name.toLowerCase(),
          )
            .join(" ")
            .includes(ser.toLowerCase()),
      );
    });
    return array;
  }
  array = stock.filter((s) => {
    return (
      s.StockItem.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      s.StockItem.sku.toLowerCase().includes(searchValue.toLowerCase()) ||
      s.Warehouse.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      s.StockItem.StockItemGroups.map((ss) =>
        ss.StockItemGroupName.name.toLowerCase(),
      )
        .join(" ")
        .includes(searchValue.toLowerCase())
    );
  });
  return array;
}

const alertFilter = async (stock) => {
  var array = stock.filter((s) => {
    return (s.reserved && s.reserved >= s.free) || s.free < s.alertQuantity;
  });
  return array;
};

const manufacturingRecipeFilter = (stock, selectedRecipes) => {
  if (!selectedRecipes?.length) {
    return stock;
  }

  const recipeStockItemIds = selectedRecipes.reduce((ids, recipe) => {
    (recipe.ManufacturingRecipeStockItems || []).forEach((recipeItem) => {
      const stockItemId = recipeItem.StockItemId || recipeItem.StockItem?.id;
      if (stockItemId) {
        ids.add(String(stockItemId));
      }
    });
    return ids;
  }, new Set());

  return stock.filter((s) => {
    const stockItemId = s.StockItemId || s.StockItem?.id;
    return recipeStockItemIds.has(String(stockItemId));
  });
};

export default function StockModule(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchList, setSearchList] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [tagSearch, setTagSearch] = useState();
  const [onlySelectedTags, setOnlySelectedTags] = useState();
  const [autocompKey, setAutocompKey] = useState("sfsadfas");
  const [useAlertFilter, setUseAlertFilter] = useState(false);
  const [manufacturingRecipes, setManufacturingRecipes] = useState([]);
  const [selectedManufacturingRecipes, setSelectedManufacturingRecipes] =
    useState([]);
  const [recipeAutocompKey, setRecipeAutocompKey] = useState("recipes");
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [reservationsAdded, setReservationsAdded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderList, setOrderList] = useState();
  const [resupplyOrderList, setResupplyOrderList] = useState();
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState();
  const [stock, setStock] = useState();
  const [warehouseLocation, setWarehouseLocation] = useState();
  const dispatch = useDispatch();

  const headCells = [
    {
      id: "StockItem.sku",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.sku"),
    },
    {
      id: "StockItem.name",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.name"),
    },
    {
      id: "StockItem.StockItemGroups",
      numeric: false,
      disablePadding: false,
      label: "Tags",
    },
    {
      id: "reserved",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.reservations"),
    },
    {
      id: "free",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.free"),
    },
    {
      id: "total",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.total"),
    },
    {
      id: "alertQuantity",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.alertQuantity"),
    },
    {
      id: "incoming",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.incoming"),
    },
    {
      id: "Warehouse.name",
      numeric: false,
      disablePadding: false,
      label: t("stockModule.warehouse"),
    },
    {
      id: "updatedAt",
      numeric: false,
      disablePadding: false,
      label: t("updatedAt"),
    },
  ];

  const addStockQuantity = (data) => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        stock_item: data,
      }),
    };
    fetch(server + "/add_stock_quantity", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          window.location.reload();
        }
        // setLoading(false);
      });
  };

  const getOrders = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
      }),
    };
    fetch(server + "/get_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setOrderList(result.orders);
        }
      })
      .catch((e) => {});
  };

  const getResupplyOrders = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    };
    fetch(server + "/get_resupply_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setResupplyOrderList(result.orders);
        }
      })
      .catch((e) => {});
  };

  const getStockQuantities = (ser_val) => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        includePacking: true,
      }),
    };
    fetch(server + "/get_stock_quantity", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setStock(result.stock);
          // setSearchList(result.stock);
        }
        // setLoading(false);
      });
  };

  const getManufacturingRecipes = () => {
    setRecipesLoading(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    };
    fetch(server + "/get_manufacturing_recipes", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          const recipes = Array.isArray(result.manufacturingRecipes)
            ? result.manufacturingRecipes
            : [];
          setManufacturingRecipes(recipes);

          const savedSearch = getFromStorage(STOCK_SEARCH_ID);
          if (savedSearch) {
            try {
              const savedFilters = JSON.parse(savedSearch);
              if (Array.isArray(savedFilters.selectedManufacturingRecipeIds)) {
                setSelectedManufacturingRecipes(
                  recipes.filter((recipe) =>
                    savedFilters.selectedManufacturingRecipeIds.some(
                      (recipeId) => String(recipeId) === String(recipe.id),
                    ),
                  ),
                );
              }
            } catch (error) {}
          }
        }
      })
      .catch(() => {
        setManufacturingRecipes([]);
      })
      .finally(() => {
        setRecipesLoading(false);
      });
  };

  const editRow = (data) => {
    setEdit(data);
  };

  useEffect(() => {
    getOrders();
    getResupplyOrders();
    getStockQuantities();
    getWarehouses(setWarehouseLocation, dispatch);
    getManufacturingRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const filterResults = async (val) => {
    search(stock, searchValue).then((arr) => {
      if (arr && arr.length && arr[0].reserved === undefined) {
        arr.forEach((a, i) => (arr[i].reserved = 0));
        orderList.forEach((o) => {
          if (o.genericDevices && o.genericDevices.length) {
            o.genericDevices.forEach((g) => {
              var idx = arr.findIndex(
                (s) => s.StockItem.id === g.id && g.type !== "service",
              );
              if (
                idx !== -1 &&
                arr[idx] &&
                (o.paymentStatus === "waiting" ||
                  o.paymentStatus === "invoiced" ||
                  o.paymentStatus === "deposit" ||
                  o.paymentStatus === "completed")
              ) {
                arr[idx].reserved = arr[idx].reserved + g.count;
              }
            });
          }
        });
      }
      if (arr && arr.length && arr[0].incoming === undefined) {
        arr.forEach((a, i) => (arr[i].incoming = 0));
        resupplyOrderList.forEach((o) => {
          if (
            o.genericDevices &&
            o.genericDevices.length &&
            o.paymentStatus !== "delivered" &&
            o.paymentStatus !== "cancelled"
          ) {
            o.genericDevices.forEach((g) => {
              var allstock = arr.filter((s) => s.StockItem.id === g.id);
              var idx = -1;
              // The Resupplyorder amount will be added to one warehouse stock, prefer mailhouse
              // Just pick anything at first
              idx = arr.findIndex((s) => s.StockItem.id === g.id);
              if (allstock.length > 1) {
                if (
                  o.buyer &&
                  o.buyer === "estonia" &&
                  allstock.find((s) => s.Warehouse.name === "estonia")
                ) {
                  idx = arr.findIndex(
                    (s) =>
                      s.StockItem.id === g.id && s.Warehouse.name === "estonia",
                  );
                } else if (
                  allstock.find((s) => s.Warehouse.name === "mailhouse")
                ) {
                  idx = arr.findIndex(
                    (s) =>
                      s.StockItem.id === g.id &&
                      s.Warehouse.name === "mailhouse",
                  );
                } else if (
                  allstock.find((s) => s.Warehouse.name === "vantaa")
                ) {
                  idx = arr.findIndex(
                    (s) =>
                      s.StockItem.id === g.id && s.Warehouse.name === "vantaa",
                  );
                }
              }
              if (idx !== -1 && arr[idx]) {
                arr[idx].incoming = (arr[idx]?.incoming || 0) + g.count;
              }
              if (o.Shipments && o.Shipments.length) {
                // Reduce the delivered ones and those which will be directed towards different warehouse
                var cnt = o.Shipments.reduce(
                  (prev, b) =>
                    prev +
                    ((b.status === "delivered" &&
                      b.genericDevices.find((ggg) => ggg.id === g.id)) ||
                    ((b.status === "transit" || b.status === "waiting") &&
                      b.shippingInfo?.companyName !== arr[idx]?.Warehouse?.name)
                      ? b.genericDevices.find((ggg) => ggg.id === g.id).count
                      : 0),
                  0,
                );
                if (cnt && arr[idx] && arr[idx].incoming) {
                  arr[idx].incoming = arr[idx].incoming - cnt;
                }
                // Check if some shipments need to be added to different stock quantity
                if (
                  o.Shipments.find(
                    (s) =>
                      s.shippingInfo?.companyName !==
                        arr[idx]?.Warehouse?.name &&
                      (s.status === "transit" || s.status === "waiting"),
                  )
                ) {
                  o.Shipments.forEach((ship) => {
                    if (
                      ship.status !== "transit" &&
                      ship.status !== "waiting"
                    ) {
                      return;
                    }
                    var cc = arr.findIndex(
                      (s) =>
                        s.StockItem.id === g.id &&
                        s?.Warehouse?.name === ship.shippingInfo?.companyName,
                    );

                    if (cc !== -1) {
                      arr[cc].incoming = ship.genericDevices.find(
                        (ge) => ge.id === g.id,
                      ).count;
                    }
                  });
                }
              }
            });
          }
        });
      }
      if (val) {
        setStock(arr);
        setLoading(false);
        // Do this only once
        var x = getFromStorage(STOCK_SEARCH_ID);
        if (x) {
          try {
            var obj = JSON.parse(x);
            if (obj.selectedWarehouses) {
              setSelectedWarehouses(obj.selectedWarehouses);
            }
            if (obj.tagSearch) {
              setTagSearch(obj.tagSearch);
            }
            if (obj.onlySelectedTags) {
              setOnlySelectedTags(obj.onlySelectedTags);
            }
            if (obj.searchValue) {
              setSearchValue(obj.searchValue);
            }
            if (obj.useAlertFilter) {
              setUseAlertFilter(obj.useAlertFilter);
            }
          } catch (error) {}
        }
      }

      if (selectedWarehouses && selectedWarehouses.length) {
        arr = arr.filter((s) => {
          return selectedWarehouses.find((f) => f === s.Warehouse.name);
        });
      }
      if (tagSearch && tagSearch.length) {
        if (onlySelectedTags) {
          arr = arr.filter((s) => {
            return (
              s.StockItem.StockItemGroups.length === tagSearch.length &&
              tagSearch.every((ta) =>
                s.StockItem.StockItemGroups.find(
                  (f) =>
                    f.StockItemGroupName.name.toLowerCase() ===
                    ta.toLowerCase(),
                ),
              )
            );
          });
        } else {
          arr = arr.filter((s) => {
            return tagSearch.some((ta) =>
              s.StockItem.StockItemGroups.find(
                (f) =>
                  f.StockItemGroupName.name.toLowerCase() === ta.toLowerCase(),
              ),
            );
          });
        }
      }

      arr = manufacturingRecipeFilter(arr, selectedManufacturingRecipes);

      if (useAlertFilter) {
        alertFilter(arr).then((ar) => {
          setSearchList(ar);
        });
      } else {
        setSearchList(arr);
      }
    });
  };

  useEffect(() => {
    if (orderList && resupplyOrderList && stock && !reservationsAdded) {
      filterResults(true);
      setReservationsAdded(true);
    }
    // eslint-disable-next-line
  }, [orderList, resupplyOrderList, stock]);

  useEffect(() => {
    if (stock && orderList && (searchValue || searchValue === "")) {
      filterResults();
    }
    // eslint-disable-next-line
  }, [
    searchValue,
    useAlertFilter,
    selectedWarehouses,
    tagSearch,
    onlySelectedTags,
    selectedManufacturingRecipes,
  ]);

  const applySearch = (new_val, key, setter) => {
    var x = getFromStorage(STOCK_SEARCH_ID);
    var obj = {};
    if (x) {
      try {
        obj = JSON.parse(x);
      } catch (error) {}
    }
    if (Array.isArray(new_val)) {
      new_val = new_val.filter((f) => f !== null);
    }
    saveToStorage(
      STOCK_SEARCH_ID,
      JSON.stringify({
        ...obj,
        [key]: new_val,
      }),
    );
    setter(new_val);
  };

  const applyRecipeSearch = (recipes) => {
    var x = getFromStorage(STOCK_SEARCH_ID);
    var obj = {};
    if (x) {
      try {
        obj = JSON.parse(x);
      } catch (error) {}
    }
    saveToStorage(
      STOCK_SEARCH_ID,
      JSON.stringify({
        ...obj,
        selectedManufacturingRecipeIds: recipes.map((recipe) => recipe.id),
      }),
    );
    setSelectedManufacturingRecipes(recipes);
  };

  return (
    <Grid container spacing={3}>
      <EditStockItemDialog
        edit={edit}
        setEdit={setEdit}
        addStockQuantity={addStockQuantity}
        orders={orderList}
        resupplys={resupplyOrderList}
        otherWarehouses={stock}
      />
      <AddStockItemDialog
        open={add}
        close={() => {
          setAdd(false);
        }}
        addStockQuantity={addStockQuantity}
      />
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ marginBottom: 10 }}>
          <Grid container spacing={3}>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  setAdd(true);
                }}
              >
                + {t("stockModule.addNewItem")}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  window.location.replace("/cpanel/stock/modify");
                }}
              >
                {t("stockModule.modifyStock")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("stock.header")}</Title>

          <TextField
            label={loading ? "Loading..." : t("search")}
            placeholder={t("stockModule.searchPlaceholder")}
            variant="outlined"
            disabled={loading}
            value={searchValue}
            InputLabelProps={{ shrink: !!searchValue ? true : undefined }}
            onChange={(e) => {
              applySearch(e.target.value || "", "searchValue", setSearchValue);
            }}
            style={{ marginBottom: 10 }}
          />
          <Grid
            item
            container
            xs={12}
            justify="space-between"
            alignItems="flex-start"
          >
            <Grid
              container
              item
              xs={12}
              md={6}
              style={{
                border: "1px solid #cbcbcb",
                borderRadius: 4,
                padding: 10,
                marginBottom: 10,
              }}
            >
              <Grid item xs={11}>
                <Typography>{t("stockModule.warehouse")}</Typography>
                {warehouseLocation ? (
                  warehouseLocation.map((o, i) => (
                    <FormControlLabel
                      key={o.value + JSON.stringify(selectedWarehouses)}
                      control={
                        <Checkbox
                          color={"primary"}
                          style={{
                            color: o.color || "",
                          }}
                          onChange={(e, v) => {
                            applySearch(
                              v
                                ? [...selectedWarehouses, o.value]
                                : [
                                    ...selectedWarehouses.filter(
                                      (f) => f !== o.value,
                                    ),
                                  ],
                              "selectedWarehouses",
                              setSelectedWarehouses,
                            );
                          }}
                          defaultChecked={
                            !!selectedWarehouses.find((f) => f === o.value)
                          }
                        />
                      }
                      label={o.label}
                    />
                  ))
                ) : (
                  <CircularProgress />
                )}
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
            <Grid container item xs={12} md={6} spacing={1}>
              <Grid item xs={12}>
                <Autocomplete
                  key={recipeAutocompKey}
                  options={manufacturingRecipes.filter(
                    (recipe) =>
                      !selectedManufacturingRecipes.find(
                        (selectedRecipe) => selectedRecipe.id === recipe.id,
                      ),
                  )}
                  getOptionLabel={(option) => option?.name || ""}
                  getOptionSelected={(option, value) =>
                    option?.id === value?.id
                  }
                  loading={recipesLoading}
                  loadingText={t("common.loading", "Loading...")}
                  noOptionsText={
                    recipesLoading
                      ? t("common.loading", "Loading...")
                      : t("manufacturing.noRecipesFound", "No recipes found")
                  }
                  onChange={(_, value) => {
                    if (!value?.id) {
                      return;
                    }
                    applyRecipeSearch([...selectedManufacturingRecipes, value]);
                    setRecipeAutocompKey(Date.now());
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(
                        "manufacturing.filterByRecipe",
                        "Filter by manufacturing recipe",
                      )}
                      placeholder={t(
                        "manufacturing.selectRecipe",
                        "Select a recipe",
                      )}
                      variant="outlined"
                    />
                  )}
                />
                <Grid item xs={12} container>
                  {selectedManufacturingRecipes.map((recipe) => (
                    <Grid key={recipe.id} item style={{ margin: "6px" }}>
                      <Button
                        style={{
                          fontSize: 12,
                          textAlign: "center",
                          backgroundColor: "lightgrey",
                          padding: 3,
                          borderRadius: 10,
                        }}
                        onClick={() => {
                          applyRecipeSearch(
                            selectedManufacturingRecipes.filter(
                              (selectedRecipe) =>
                                selectedRecipe.id !== recipe.id,
                            ),
                          );
                        }}
                        endIcon={<Clear />}
                      >
                        {recipe.name}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TagNameSearchAutocomplete
                  reset={autocompKey}
                  onChange={(e, v) => {
                    if (!v || !v.name) {
                      return;
                    }
                    applySearch(
                      [...new Set([...(tagSearch || []), v.name])],
                      "tagSearch",
                      setTagSearch,
                    );
                    setAutocompKey(Date.now());
                  }}
                />
                <Grid item xs={12} container>
                  {tagSearch
                    ? tagSearch.map((ta, i) => (
                        <Grid key={i} item style={{ margin: "6px" }}>
                          <Button
                            style={{
                              fontSize: 12,
                              textAlign: "center",
                              backgroundColor: "lightgrey",
                              padding: 3,
                              borderRadius: 10,
                            }}
                            onClick={(e) => {
                              applySearch(
                                [...tagSearch.filter((tt) => tt !== ta)],
                                "tagSearch",
                                setTagSearch,
                              );
                            }}
                            endIcon={<Clear />}
                          >
                            {ta}
                          </Button>
                        </Grid>
                      ))
                    : null}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  key={"onlySelectedTags" + onlySelectedTags}
                  control={
                    <Checkbox
                      color="primary"
                      defaultChecked={onlySelectedTags}
                      onChange={() => {
                        applySearch(
                          !onlySelectedTags,
                          "onlySelectedTags",
                          setOnlySelectedTags,
                        );
                      }}
                    />
                  }
                  label={t("stockModule.exactTagMatch")}
                ></FormControlLabel>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  key={"useAlertFilter" + useAlertFilter}
                  control={
                    <Checkbox
                      color="primary"
                      defaultChecked={useAlertFilter}
                      onChange={() => {
                        applySearch(
                          !useAlertFilter,
                          "useAlertFilter",
                          setUseAlertFilter,
                        );
                      }}
                    />
                  }
                  label={t("stockModule.onlyAlerts")}
                ></FormControlLabel>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              borderTop: "1px solid rgb(203,203,203)",
              marginTop: 20,
              marginBottom: 50,
            }}
          ></Grid>

          {searchList ? (
            <SortableTable
              header={
                "stock_report_" + new Date().toISOString().substring(0, 10)
              }
              alternateColors={true}
              downloadButton={true}
              headCells={headCells}
              // disablePagination={true,
              defaultSortRow={"StockItem.sku"}
              handleRowClick={editRow}
              // handleScrollClick={showDeviceNewTab}
              tableData={searchList}
              rowsPerPage={200}
              getRowStyle={(data, index) => {
                var obj = { cursor: "pointer" };
                if (data.reserved && data.reserved >= data.free) {
                  return { ...obj, backgroundColor: "red" };
                }
                if (data.alertQuantity > data.free) {
                  return { ...obj, backgroundColor: "yellow" };
                }
                if (Object.keys(obj).length === 1 && index % 2 === 0) {
                  obj.backgroundColor = "rgba(245, 245, 245, 1)";
                }
                return obj;
              }}
              imageData={"StockItem.imageUrl"}
              // Takes the header id as identifier and
              specialDataFormatting={[
                {
                  id: "StockItem.sku",
                  formatReturnsElement: true,
                  format: (data, imgdata) => {
                    if (imgdata === "json") {
                      return data;
                    }
                    return (
                      <Grid container alignItems="center">
                        {imgdata ? (
                          <Grid item>
                            <img
                              alt={data + "-image"}
                              src={imgdata}
                              style={{
                                width: 50,
                                padding: 5,
                                borderRadius: 15,
                              }}
                            />
                          </Grid>
                        ) : null}
                        <Grid item>{data}</Grid>
                      </Grid>
                    );
                  },
                },
                {
                  id: "StockItem.StockItemGroups",
                  formatReturnsElement: true,
                  format: (data, e) => {
                    return data
                      .sort((a, b) => {
                        if (
                          a.StockItemGroupName.name > b.StockItemGroupName.name
                        ) {
                          return 1;
                        }
                        return -1;
                      })
                      .map((d, i) =>
                        e === "json" ? (
                          d.StockItemGroupName.name
                        ) : (
                          <Typography
                            key={i}
                            style={{
                              fontSize: 12,
                              textAlign: "center",
                              backgroundColor: "lightgrey",
                              padding: 3,
                              borderRadius: 10,
                              marginBottom: 1,
                              zIndex: 9999,
                            }}
                            onClick={(e) => {
                              applySearch(
                                [
                                  ...new Set([
                                    ...(tagSearch || []),
                                    d.StockItemGroupName.name,
                                  ]),
                                ],
                                "tagSearch",
                                setTagSearch,
                              );
                              setAutocompKey(Date.now());
                              // Hack to prevent opening the edit window
                              setEdit();
                              setTimeout(() => {
                                setEdit();
                              }, 1);
                            }}
                          >
                            {d.StockItemGroupName.name}
                          </Typography>
                        ),
                      );
                  },
                },
                {
                  id: "Warehouse.name",
                  format: (d) => {
                    return warehouseLocation
                      ? warehouseLocation.find((a) => a.value === d)?.label ||
                          ""
                      : "";
                  },
                },
                {
                  id: "updatedAt",
                  format: (d) => {
                    return formatTimeWithTZOffset(d, ECABIN);
                  },
                },
              ]}
            />
          ) : loading ? (
            <CircularProgress />
          ) : undefined}
        </Paper>
      </Grid>
    </Grid>
  );
}
